<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import {
  formatFunnelValue,
  formatFieldValue,
  hasFieldValue,
} from 'dashboard/helper/funnelHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FunnelCardEditDialog from 'dashboard/components-next/funnels/FunnelCardEditDialog.vue';

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  archived: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const accountId = useMapGetter('getCurrentAccountId');
const funnelGetter = useMapGetter('funnels/getFunnel');

const editDialogRef = ref(null);
const isArchiving = ref(false);

const funnel = computed(() => funnelGetter.value(props.card.funnel_id));

const contactName = computed(() => props.card.contact?.name || '');
const assigneeName = computed(() => props.card.assignee?.name || '');
const formattedValue = computed(() => formatFunnelValue(props.card.value));

// Only the funnel's fields that have a value on this card, in field order, so
// the card stays compact and shows nothing for unfilled fields.
const filledFields = computed(() => {
  const values = props.card.custom_field_values || {};
  return [...(funnel.value?.fields || [])]
    .sort((a, b) => a.position - b.position)
    .filter(field => hasFieldValue(values[field.key]))
    .map(field => ({
      id: field.id,
      name: field.name,
      value: formatFieldValue(field, values[field.key]),
    }));
});

const openConversation = () => {
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: props.card.conversation_id,
    },
  });
};

const openEdit = () => {
  editDialogRef.value?.open();
};

// Archiving is reversible and the card keeps holding its conversation's slot in
// the funnel, so it does not warrant a destructive-action confirmation dialog.
const archive = async () => {
  if (isArchiving.value) return;
  isArchiving.value = true;
  try {
    await store.dispatch('funnels/archiveCard', {
      funnelId: props.card.funnel_id,
      cardId: props.card.id,
    });
    useAlert(t('FUNNELS.CARD.ARCHIVE.SUCCESS'));
  } catch (error) {
    useAlert(t('FUNNELS.CARD.ARCHIVE.ERROR'));
  } finally {
    isArchiving.value = false;
  }
};

// Counterpart of archive: the card leaves the archived list and returns to the
// board, for this operator and — through funnel.card.unarchived — for everyone
// else watching the funnel.
const unarchive = async () => {
  if (isArchiving.value) return;
  isArchiving.value = true;
  try {
    await store.dispatch('funnels/unarchiveCard', {
      funnelId: props.card.funnel_id,
      cardId: props.card.id,
    });
    useAlert(t('FUNNELS.CARD.UNARCHIVE.SUCCESS'));
  } catch (error) {
    useAlert(t('FUNNELS.CARD.UNARCHIVE.ERROR'));
  } finally {
    isArchiving.value = false;
  }
};
</script>

<!-- The root element carries data-card-id as a stable hook for end-to-end tests:
  without it the only way to reach a card is by Tailwind classes, which the stage
  column shares (both carry rounded-lg/border-n-weak), and that ambiguity made an
  E2E run click the archive button of the wrong card. Keep it on the root — a
  comment inside <template> would give the component two root nodes. -->
<template>
  <div
    :data-card-id="card.id"
    class="flex flex-col w-full gap-2 p-3 border rounded-lg bg-n-background border-n-weak hover:border-n-strong"
  >
    <div class="flex items-center w-full gap-2">
      <button
        type="button"
        class="flex items-center flex-1 min-w-0 gap-2 text-left"
        @click="openConversation"
      >
        <Avatar :name="contactName" :size="24" rounded-full />
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ contactName }}
        </span>
      </button>
      <NextButton
        v-if="funnel && !archived"
        v-tooltip.top="t('FUNNELS.CARD.EDIT.ACTION')"
        icon="i-lucide-pencil"
        slate
        xs
        ghost
        class="shrink-0"
        @click="openEdit"
      />
      <NextButton
        v-if="!archived"
        v-tooltip.top="t('FUNNELS.CARD.ARCHIVE.ACTION')"
        icon="i-lucide-archive"
        slate
        xs
        ghost
        :is-loading="isArchiving"
        :disabled="isArchiving"
        class="shrink-0"
        @click="archive"
      />
      <NextButton
        v-else
        v-tooltip.top="t('FUNNELS.CARD.UNARCHIVE.ACTION')"
        icon="i-lucide-archive-restore"
        slate
        xs
        ghost
        :is-loading="isArchiving"
        :disabled="isArchiving"
        class="shrink-0"
        @click="unarchive"
      />
    </div>

    <div
      v-if="assigneeName"
      class="flex items-center gap-1.5 text-xs text-n-slate-11"
    >
      <span>{{ t('FUNNELS.CARD.ASSIGNED_TO') }}</span>
      <span class="truncate text-n-slate-12">{{ assigneeName }}</span>
    </div>

    <span v-if="formattedValue" class="text-xs font-medium text-n-slate-11">
      {{ formattedValue }}
    </span>

    <dl v-if="filledFields.length" class="flex flex-col gap-0.5">
      <div
        v-for="field in filledFields"
        :key="field.id"
        class="flex items-baseline gap-1.5 text-xs"
      >
        <dt class="text-n-slate-10 shrink-0">{{ `${field.name}:` }}</dt>
        <dd class="truncate text-n-slate-11">{{ field.value }}</dd>
      </div>
    </dl>

    <FunnelCardEditDialog
      v-if="funnel"
      ref="editDialogRef"
      :card="card"
      :funnel="funnel"
    />
  </div>
</template>
